<template>
  <RevInputSelect
    v-if="hasMultipleLanguageOptions"
    id="select-language"
    :data-qa="`dropdown-lang-${modelValue}`"
    :label="i18n(translations.selectLanguage)"
    :model-value
    :options="languagesOptions"
    :size="sizeFromVariant"
    @update:model-value="$emit('update:modelValue', $event)"
  />
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { BCP_47_LOCALES, type Bcp47Locale } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'
import { RevInputSelect } from '@ds/components/InputSelect'
import type { Variants } from '@ds/components/InputSelect/InputSelect.constant'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import translations from './SelectLanguage.translations'

const props = withDefaults(
  defineProps<{
    modelValue: Bcp47Locale | ''
    variant?: Variants
  }>(),
  { variant: 'default' },
)

defineEmits(['update:modelValue'])

const { marketplace } = useMarketplace()
const { canSeeChineseLanguage } = useSellerInfoStore()
const i18n = useI18n()

const languagesOptions = computed(() => {
  const languages =
    marketplace.markets.map(({ countryCode, defaultLocale }) => ({
      label:
        i18n(translations[`languageOptionLabel${countryCode}`]) ?? countryCode,
      value: toBcp47Locale(defaultLocale),
    })) || []

  if (canSeeChineseLanguage) {
    languages.push({
      value: BCP_47_LOCALES.zh_HANS,
      label: i18n(translations.languageOptionLabelCN),
    })
  }

  return languages.sort((a, b) => a.label.localeCompare(b.label))
})

const hasMultipleLanguageOptions = computed(
  () => languagesOptions.value.length > 1,
)

const sizeFromVariant = computed(() => {
  switch (props.variant) {
    case 'tiny':
      return 'small'
    default:
      return 'medium'
  }
})
</script>
